<script setup lang="ts">
// Banner do Clube de Fidelidade na sidebar (Figma "Sidebar / Clube de
// Fidelidade", 233x129). Fundo em gradiente + glows em CSS, brasão central
// vazando pra cima e 4 estrelas soltas por cima com flutuação própria. O CTA
// "Conheça o Clube" pende 14px abaixo da caixa do banner.

const emit = defineEmits<{
  click: [];
}>();

interface BannerStar {
  id: number;
  top: string;
  left?: string;
  right?: string;
  width: string;
  // Rotação de repouso da arte (a flutuação soma por cima).
  base: string;
  // Amplitude de rotação da flutuação (sinal alterna pra vizinhas não
  // balançarem em fase).
  rot: string;
  dur: string;
  delay: string;
}

// Posições vêm do Figma (banner de 233px): estrela grande no canto superior
// esquerdo, a maior borrada vazando o canto superior direito e as pequenas nas
// beiradas de baixo.
const stars: BannerStar[] = [
  {
    id: 1,
    top: '-17px',
    left: '4.7%',
    width: '51px',
    base: '0deg',
    rot: '6deg',
    dur: '2.3s',
    delay: '0s',
  },
  {
    id: 2,
    top: '72px',
    left: '2.5%',
    width: '32px',
    base: '-45.7deg',
    rot: '-8deg',
    dur: '1.9s',
    delay: '-0.9s',
  },
  {
    id: 3,
    top: '59px',
    right: '5.5%',
    width: '26px',
    base: '0deg',
    rot: '7deg',
    dur: '2.75s',
    delay: '-1.7s',
  },
  {
    id: 4,
    top: '-18px',
    right: '3.5%',
    width: '49px',
    base: '-58.7deg',
    rot: '-5deg',
    dur: '2.5s',
    delay: '-2.2s',
  },
];
</script>

<template>
  <div
    class="loyalty-banner"
    @click="emit('click')"
  >
    <span class="loyalty-banner-glow loyalty-banner-glow--gold" />
    <span class="loyalty-banner-glow loyalty-banner-glow--blue" />

    <div class="loyalty-banner-logo">
      <NuxtImg
        alt="Clube de Fidelidade"
        draggable="false"
        height="176"
        loading="eager"
        :src="getAssetsUrlForCDN('/assets/images/sidebar-v2/clube-logo.webp')"
        width="176"
      />
    </div>

    <NuxtImg
      v-for="star in stars"
      :key="star.id"
      alt=""
      aria-hidden="true"
      class="loyalty-banner-star"
      draggable="false"
      :src="
        getAssetsUrlForCDN(`/assets/images/sidebar-v2/star-${star.id}.webp`)
      "
      :style="{
        top: star.top,
        left: star.left,
        right: star.right,
        width: star.width,
        '--star-base': star.base,
        '--star-rot': star.rot,
        animationDuration: star.dur,
        animationDelay: star.delay,
      }"
    />

    <span class="loyalty-banner-cta">
      <span>Conheça o Clube</span>
      <NuxtIcon
        class="loyalty-banner-cta-chevron"
        name="menu-lateral-v2/chevron-right"
      />
    </span>
  </div>
</template>

<style scoped lang="scss">
.loyalty-banner {
  // 14px extra embaixo pra acomodar o CTA que pende pra fora da caixa.
  @apply relative w-full h-[129px] mb-[14px] rounded-[18px] cursor-pointer select-none;

  // Fundo num pseudo-elemento pra que o brilho de hover possa ser aplicado
  // por filho: tudo brilha, menos o CTA "Conheça o Clube".
  &::before {
    @apply absolute inset-0 rounded-[18px] pointer-events-none;
    content: '';
    background:
      linear-gradient(
        90deg,
        rgba(255, 255, 255, 0.04),
        rgba(255, 255, 255, 0.04)
      ),
      linear-gradient(
        180deg,
        #040e2e 0%,
        #0a1a4e 55%,
        rgba(17, 17, 188, 0.85) 100%
      );
  }

  @media (hover: hover) {
    &:hover {
      &::before,
      .loyalty-banner-glow,
      .loyalty-banner-logo {
        filter: brightness(1.1);
      }

      .loyalty-banner-star {
        filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.35)) brightness(1.1);
      }
    }
  }
}

.loyalty-banner-glow {
  @apply absolute pointer-events-none rounded-full;

  &--gold {
    left: 32.6%;
    top: 7px;
    width: 81px;
    height: 47px;
    background: radial-gradient(
      ellipse at center,
      rgba(245, 196, 81, 0.32) 0%,
      rgba(245, 196, 81, 0) 70%
    );
    transform: scale(2.2);
  }

  &--blue {
    left: 7%;
    top: 59px;
    width: 200px;
    height: 70px;
    background: radial-gradient(
      ellipse at center,
      rgba(20, 20, 221, 0.5) 0%,
      rgba(20, 20, 221, 0) 70%
    );
    transform: scale(1.6);
  }
}

.loyalty-banner-logo {
  @apply absolute overflow-hidden pointer-events-none;
  left: 50%;
  top: -9px;
  width: 176px;
  height: 135px;
  transform: translateX(-50%);

  img {
    @apply block w-[176px] h-[176px] max-w-none;
    margin-top: -19px;
    filter: drop-shadow(0 8px 18px rgba(0, 0, 0, 0.45));
  }
}

.loyalty-banner-star {
  @apply absolute pointer-events-none select-none;
  height: auto;
  z-index: 1;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.35));
  /* O main.scss zera `transition` globalmente — movimento aqui é animation. */
  animation: loyalty-star-float 4s ease-in-out infinite alternate;
  will-change: transform;
}

.loyalty-banner-cta {
  @apply absolute z-[2] flex items-center gap-[6px] rounded-[99px] pl-[14px] pr-[12px] py-[7px] text-white text-[11px] font-bold leading-normal whitespace-nowrap;
  left: 50%;
  top: 115px;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
}

.loyalty-banner-cta-chevron {
  @apply shrink-0;

  :deep(svg) {
    @apply w-[11px] h-[11px];
  }
}

@media (prefers-reduced-motion: reduce) {
  .loyalty-banner-star {
    animation: none;
    transform: rotate(var(--star-base, 0deg));
  }
}

@keyframes loyalty-star-float {
  from {
    transform: translateY(-3%)
      rotate(calc(var(--star-base, 0deg) - var(--star-rot, 6deg)));
  }

  to {
    transform: translateY(6%)
      rotate(calc(var(--star-base, 0deg) + var(--star-rot, 6deg)));
  }
}
</style>
